'use client';

/* ──────────────────────────────────────────────
 *  CustomerInfoFields — bloc identité client réutilisable
 *  (prénom / nom / email / téléphone) partagé entre le
 *  tunnel de réservation (CheckoutStep) et le tunnel
 *  d'achat de carte cadeau (PurchaseTunnel). Markup +
 *  styles `bt-*` + PhoneInput identiques.
 * ────────────────────────────────────────────── */

import { PhoneInput } from './PhoneInput';

export type CustomerInfoField = 'firstname' | 'lastname' | 'email' | 'phone';

export interface CustomerInfoValues {
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
}

export interface CustomerInfoErrors {
  firstname?: string;
  lastname?: string;
  email?: string;
  phone?: string;
}

export interface CustomerInfoLabels {
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  phone_country_code?: string;
  phone_placeholder?: string;
}

interface CustomerInfoFieldsProps {
  values: CustomerInfoValues;
  errors?: CustomerInfoErrors;
  onChange: (field: CustomerInfoField, value: string) => void;
  onBlur?: (field: CustomerInfoField) => void;
  disabled?: boolean;
  locale?: string;
  flagBaseUrl?: string;
  labels: CustomerInfoLabels;
  /** Le téléphone est-il obligatoire (astérisque) ? Réservation = oui, carte cadeau = non. */
  phoneRequired?: boolean;
  /** Préfixe des `id` (évite les collisions si deux blocs sur une même page). */
  idPrefix?: string;
}

export function CustomerInfoFields({
  values,
  errors = {},
  onChange,
  onBlur,
  disabled,
  locale,
  flagBaseUrl,
  labels,
  phoneRequired = true,
  idPrefix = 'ci',
}: CustomerInfoFieldsProps) {
  return (
    <div className="bt-checkout__fields bt-checkout__fields--2col">
      <div className="bt-field">
        <label className="bt-field__label" htmlFor={`${idPrefix}-firstname`}>
          {labels.firstname}
          <span className="bt-field__required"> *</span>
        </label>
        <input
          id={`${idPrefix}-firstname`}
          autoComplete="given-name"
          type="text"
          className={`bt-field__input ${errors.firstname ? 'bt-field__input--error' : ''}`}
          value={values.firstname}
          onChange={(e): void => onChange('firstname', e.target.value)}
          onBlur={(): void => onBlur?.('firstname')}
          disabled={disabled}
        />
        {errors.firstname && <span className="bt-field__error">{errors.firstname}</span>}
      </div>

      <div className="bt-field">
        <label className="bt-field__label" htmlFor={`${idPrefix}-lastname`}>
          {labels.lastname}
          <span className="bt-field__required"> *</span>
        </label>
        <input
          id={`${idPrefix}-lastname`}
          autoComplete="family-name"
          type="text"
          className={`bt-field__input ${errors.lastname ? 'bt-field__input--error' : ''}`}
          value={values.lastname}
          onChange={(e): void => onChange('lastname', e.target.value)}
          onBlur={(): void => onBlur?.('lastname')}
          disabled={disabled}
        />
        {errors.lastname && <span className="bt-field__error">{errors.lastname}</span>}
      </div>

      <div className="bt-field">
        <label className="bt-field__label" htmlFor={`${idPrefix}-email`}>
          {labels.email}
          <span className="bt-field__required"> *</span>
        </label>
        <input
          id={`${idPrefix}-email`}
          autoComplete="email"
          type="email"
          className={`bt-field__input ${errors.email ? 'bt-field__input--error' : ''}`}
          value={values.email}
          onChange={(e): void => onChange('email', e.target.value)}
          onBlur={(): void => onBlur?.('email')}
          disabled={disabled}
        />
        {errors.email && <span className="bt-field__error">{errors.email}</span>}
      </div>

      <div className="bt-field">
        <label className="bt-field__label" htmlFor={`${idPrefix}-phone`}>
          {labels.phone}
          {phoneRequired && <span className="bt-field__required"> *</span>}
        </label>
        <PhoneInput
          id={`${idPrefix}-phone`}
          value={values.phone}
          onChange={(val): void => onChange('phone', val)}
          locale={locale}
          disabled={disabled}
          error={errors.phone}
          countryCodeLabel={labels.phone_country_code}
          placeholder={labels.phone_placeholder}
          flagBaseUrl={flagBaseUrl}
        />
      </div>
    </div>
  );
}
